import { type ReactNode, useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useT } from '../context/I18nContext';
import { BrandMark, ThemeToggle } from './ui';
import { LanguagePicker } from './LanguagePicker';
import { NotificationBell } from './NotificationBell';
import { InstallButton } from './InstallPrompt';
import { CampusPicker } from './CampusPicker';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useCaptureModes, type CaptureMode } from '../lib/captureModes';

// Access sets MIRROR the backend authorization policies exactly — nav visibility must match
// real access, or a hidden-but-allowed (or shown-but-403) mismatch reappears. Keep in sync
// with the policies in Program.cs.
/**
 * ⚠ These MIRROR the backend policies in Program.cs. The backend is the real boundary — this only
 * decides what's shown. If they ever disagree, a user sees a menu item that 403s, which looks like
 * a bug. Keep them in step.
 *
 * The narrow roles (Security / Warden / Librarian) are deliberately NOT in `staff`. A gate guard
 * should be able to scan a pass — that is all. They must not see the roster, the fees or the results.
 */
export const ROLES = {
  staff: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Accountant', 'Teacher',
          'LabTechnician', 'StoreKeeper', 'SportsCoordinator', 'TransportManager', 'Receptionist', 'HrManager'],
  finance: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Accountant'],
  admin: ['TrustAdmin', 'CampusAdmin'],

  /// ⚠ THE STUDENT DIRECTORY AND THE REGISTER — NOT the same list as `staff`.
  ///
  /// `staff` means "employed here" and is right for a person's OWN things: their duty, their leave,
  /// the school feed. It is the wrong list for every child's record. A storekeeper allocating
  /// uniforms and a transport manager running routes were both being handed the full student
  /// directory, the attendance register, the photo roll and the card scanner — the whole school's
  /// children — because they are staff.
  ///
  /// Neither needs it: the store page allocates through its own student picker and the library
  /// issues through its own borrower lookup, so nothing that a narrow role actually DOES goes
  /// through this menu. What went through it was browsing.
  roster: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Accountant', 'Teacher', 'Receptionist', 'HrManager'],

  /// ⚠ TRUST-WIDE, WHICH IS NARROWER THAN `admin`. `admin` is TrustAdmin + CampusAdmin, and almost
  /// everything under it is correct for both — a campus admin configures their own campus. But the
  /// cross-campus rollup is not that: it compares every campus in the trust against every other.
  /// A person hired to run one campus was being offered the other campuses' collections and results.
  trust: ['TrustAdmin'],

  /// ⚠ TWO FAMILY LISTS, BECAUSE A CHILD IS NOT THEIR PARENT. `family` was one list containing both,
  /// so a Student login was a Parent login exactly — the fee ledger, the guardian record and the
  /// consent switches included. A student sees their own school life; the money and the permissions
  /// belong to whoever is responsible for them.
  family: ['Parent'],
  familyAll: ['Parent', 'Student'],

  /// Teaching functions — marks, timetable, labs, classroom. Deliberately EXCLUDES Accountant:
  /// a finance user has no business entering exam results or running a laboratory.
  teaching: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Teacher'],

  // ---- the narrow roles: their own module, and nothing else ----
  gate: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Security'],
  hostel: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Warden'],
  library: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Librarian', 'Teacher'],
  labs: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Teacher', 'LabTechnician'],
  store: ['TrustAdmin', 'CampusAdmin', 'Principal', 'StoreKeeper'],
  sports: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Teacher', 'SportsCoordinator'],
  transportMgmt: ['TrustAdmin', 'CampusAdmin', 'Principal', 'TransportManager'],
  frontdesk: ['TrustAdmin', 'CampusAdmin', 'Principal', 'Receptionist'],
  /// HR without the fee ledger. Payroll is NOT here — it moves money, so it stays with admin.
  hr: ['TrustAdmin', 'CampusAdmin', 'Principal', 'HrManager'],

  // Everyone with a login — used only for things genuinely everyone gets (the feed).
  all: [
    'TrustAdmin', 'CampusAdmin', 'Principal', 'Accountant', 'Teacher',
    'Parent', 'Student',
    'Security', 'Warden', 'Librarian',
    'LabTechnician', 'StoreKeeper', 'SportsCoordinator', 'TransportManager', 'Receptionist', 'HrManager',
  ],
} as const;

type NavItem = {
  to: string; label: string; end?: boolean; icon: string; roles: readonly string[]; module?: string;
  /**
   * ⚠ A SECOND GATE, NARROWER THAN A MODULE. Attendance is core — no school switches it off — but
   * HOW a register is taken is a per-school choice, so the photo roll and the scanner are hidden
   * for a school that has not asked for them. Without this, every school sees a scanner and has
   * nothing to scan, which reads as broken rather than as unconfigured.
   */
  capture?: CaptureMode;
};

// Which nav routes belong to a toggleable module (must match the backend FeatureModule keys).
const ROUTE_MODULE: Record<string, string> = {
  '/transport': 'Transport', '/bus-and-pickup': 'Transport', '/vehicle-costs': 'Transport',
  '/hostel': 'Hostel',
  '/library': 'Library',
  '/exams': 'Exams', '/eligibility': 'Exams',
  '/timetable': 'Timetable',
  '/hr': 'Hr', '/my-leave': 'Hr', '/staff-attendance': 'Hr', '/staff/import': 'Hr', '/staff/photos': 'Hr', '/staff/id-cards': 'Hr',
  '/payroll': 'Payroll',
  '/certificates': 'Certificates',
  '/communication': 'Communication',
  '/approvals': 'Approvals',
  '/gate': 'Gate', '/releases': 'Gate',
  '/classroom': 'Classroom', '/homework': 'Classroom',
  '/news': 'News',

  // ⚠ Added with the five new module keys. Every one of these screens was previously un-toggleable:
  // a school could not switch them off and SuperAdmin had no entry for them in the console.
  //
  // ⚠ /exam-eligibility joins the EXISTING Exams key rather than getting its own. /eligibility was
  // already gated there and the newer screen was not — two eligibility screens, one switchable and
  // one not, is the kind of inconsistency nobody notices until a school switches Exams off and one
  // of them stays.
  '/ask': 'Assistant',
  '/at-risk': 'Outcomes', '/results': 'Outcomes',
  '/presence': 'Presence',
  '/exam-eligibility': 'Exams',
  '/invigilation': 'ExamOps', '/my-duties': 'ExamOps',
  '/question-bank': 'QuestionBank', '/paper-builder': 'QuestionBank', '/mastery': 'QuestionBank',
  // The pattern a paper is cut from is the same feature as the paper. A school that switches
  // QuestionBank off must not keep an editor for blueprints nothing can use.
  '/blueprints': 'QuestionBank',
  // The chapters the assistant drafts questions FROM. Same key as the bank deliberately: a school
  // with QuestionBank switched off must not be left holding a library nothing can read.
  '/sources': 'QuestionBank',
};

const navGroups: { heading: string | null; key?: string; items: NavItem[] }[] = [
  {
    heading: null,
    items: [
      { to: '/', label: 'Overview', end: true, roles: ROLES.all, icon: 'M3 12l9-9 9 9M5 10v10h14V10' },
      { to: '/ask', label: 'Ask Eduvora', roles: ROLES.staff, icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
      { to: '/at-risk', label: 'Students at risk', roles: ROLES.teaching, icon: 'M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' },
      { to: '/results', label: 'Class results', roles: ROLES.teaching, icon: 'M9 11H5a2 2 0 0 0-2 2v7h6zM15 3h-6v17h6zM19 8h-4v12h6v-10a2 2 0 0 0-2-2z' },
      { to: '/presence', label: 'Who is not here', roles: ROLES.teaching, icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM17 8l5 5M22 8l-5 5' },
      { to: '/exam-eligibility', label: 'Exam eligibility', roles: ROLES.teaching, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11M12 17h.01' },
      { to: '/staff-overview', label: 'Staff overview', roles: ROLES.hr, icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
      { to: '/invigilation', label: 'Invigilation', roles: ROLES.hr, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/question-bank', label: 'Question bank', roles: ROLES.teaching, icon: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z' },
      // Directly under the bank, because "where did this question come from?" is asked while
      // looking at the bank.
      { to: '/sources', label: 'Source material', roles: ROLES.teaching, icon: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z' },
      { to: '/paper-builder', label: 'Paper builder', roles: ROLES.teaching, icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8' },
      // Next to the paper builder, because that is the screen you are on when you discover the
      // pattern is wrong.
      { to: '/blueprints', label: 'Paper patterns', roles: ROLES.teaching, icon: 'M3 3h18v18H3zM3 9h18M9 9v12M13 13h4M13 17h4' },
      { to: '/mastery', label: 'What the paper showed', roles: ROLES.teaching, icon: 'M3 3v18h18M7 16l4-6 3 4 5-8' },
      { to: '/my-duties', label: 'My duty', roles: ROLES.staff, icon: 'M12 8v4l3 3M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z' },
    ],
  },
  {
    heading: 'My school', key: 'family',
    items: [
      { to: '/family', label: 'My family', roles: ROLES.family, icon: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
      { to: '/payments', label: 'Payments', roles: ROLES.family, icon: 'M3 10h18M3 10V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4M3 10v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8M7 15h4' },
      { to: '/homework', label: 'Homework & messages', roles: ROLES.familyAll, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/bus-and-pickup', label: 'Bus & pickup', roles: ROLES.familyAll, icon: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M7 20v-2M17 20v-2' },
      { to: '/permissions', label: 'Permissions', roles: ROLES.family, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/feed', label: 'From the school', roles: ROLES.all, icon: 'M4 4h16v12H5.2L4 17.2V4z' },
    ],
  },
  {
    heading: 'Students', key: 'students',
    items: [
      { to: '/students', label: 'Students', roles: ROLES.roster, icon: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z' },
      // ⚠ THESE THREE HAD ROUTES AND NO WAY IN. Bulk import, photographs and ID cards were all
      // built, all reachable only by typing the URL — a school would never have found them, and
      // "we have that, it is just not in the menu" is not a thing anyone gets to say twice.
      { to: '/students/import', label: 'Import students', roles: ROLES.admin, icon: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' },
      { to: '/students/photos', label: 'Photographs', roles: ROLES.admin, icon: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
      { to: '/students/id-cards', label: 'ID cards', roles: ROLES.admin, icon: 'M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM8 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM5 16h6M15 10h4M15 14h4' },
      { to: '/enquiries', label: 'Enquiries', roles: ROLES.frontdesk, icon: 'M3 5h18M3 12h12M3 19h6' },
      { to: '/admissions', label: 'Admissions', roles: ROLES.frontdesk, icon: 'M12 5v14M5 12h14' },
      { to: '/attendance', label: 'Attendance', roles: ROLES.roster, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/attendance/photo-roll', label: 'Photo roll', roles: ROLES.roster, capture: 'PhotoRoll', icon: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z' },
      { to: '/attendance/scan', label: 'Card scan', roles: ROLES.roster, capture: 'CardScan', icon: 'M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 8h3v3H7zM14 8h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z' },
      { to: '/attendance/settings', label: 'Attendance settings', roles: ROLES.admin, icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' },
      { to: '/attendance/face-consent', label: 'Face consent', roles: ROLES.admin, icon: 'M12 15a7 7 0 0 0-7 7M12 15a7 7 0 0 1 7 7M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM9 8h.01M15 8h.01M10 10.5s.8.8 2 .8 2-.8 2-.8' },
      { to: '/requests', label: 'Requests', roles: ROLES.staff, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/remarks', label: 'Remarks', roles: ROLES.teaching, icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
    ],
  },
  {
    heading: 'Academics', key: 'academics',
    items: [
      { to: '/academics', label: 'Academics', roles: ROLES.teaching, icon: 'M22 10L12 5 2 10l10 5 10-5zM6 12v5c0 1 2 2 6 2s6-1 6-2v-5' },
      { to: '/timetable', label: 'Timetable', roles: ROLES.teaching, icon: 'M3 4h18v18H3zM3 9h18M8 3v4M16 3v4M7 13h4v4H7z' },
      { to: '/exams', label: 'Exams', roles: ROLES.teaching, icon: 'M12 14l9-5-9-5-9 5 9 5zM12 14v7M6 10.6V16c0 1 2.7 2 6 2s6-1 6-2v-5.4' },
      { to: '/report-cards', label: 'Report cards', roles: ROLES.teaching, icon: 'M9 2h6a2 2 0 0 1 2 2v16l-5-3-5 3V4a2 2 0 0 1 2-2zM9 7h6M9 11h6' },
      { to: '/eligibility', label: 'Eligibility', roles: ROLES.teaching, icon: 'M9 11l3 3L22 4M22 12v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11' },
      { to: '/sessions', label: 'Sessions', roles: ROLES.teaching, icon: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' },
      { to: '/classroom', label: 'My classroom', roles: ROLES.teaching, icon: 'M12 6.5V20M4 6.5A2.5 2.5 0 0 1 6.5 4H12v16H6.5A2.5 2.5 0 0 1 4 17.5v-11zM20 6.5A2.5 2.5 0 0 0 17.5 4H12v16h5.5a2.5 2.5 0 0 0 2.5-2.5v-11z' },
      { to: '/labs', label: 'Laboratories', roles: ROLES.labs, icon: 'M9 2v6l-5 9a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-9V2M8 2h8M7 15h10' },
    ],
  },
  {
    heading: 'Finance', key: 'finance',
    items: [
      { to: '/fees', label: 'Fees', roles: ROLES.finance, icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' },
      { to: '/fee-admin', label: 'Fee admin', roles: ROLES.finance, icon: 'M3 3h18v4H3zM3 10h18v11H3zM8 14h8M8 17h5' },
      { to: '/approvals', label: 'Approvals', roles: ROLES.finance, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/recovery', label: 'Fee recovery', roles: ROLES.finance, icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6M3 3l18 18' },
      { to: '/fee-analytics', label: 'Fee analytics', roles: ROLES.finance, icon: 'M3 3v18h18M7 15l4-4 3 3 5-6' },
      { to: '/invoice-designer', label: 'Invoice designer', roles: ROLES.admin, icon: 'M4 4h16v16H4zM8 8h8M8 12h8M8 16h4' },
      { to: '/billing-profile', label: 'Invoice details', roles: ROLES.admin, icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M8 13h8M8 17h5' },
    ],
  },
  {
    heading: 'People', key: 'people',
    items: [
      { to: '/hr', label: 'HR', roles: ROLES.hr, icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
      { to: '/staff/import', label: 'Import staff', roles: ROLES.admin, icon: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' },
      { to: '/staff/photos', label: 'Staff photographs', roles: ROLES.admin, icon: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
      { to: '/staff/id-cards', label: 'Staff ID cards', roles: ROLES.admin, icon: 'M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM8 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM5 16h6M15 10h4M15 14h4' },
      { to: '/staff-attendance', label: 'Staff attendance', roles: ROLES.hr, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
      { to: '/my-leave', label: 'My leave', roles: ROLES.staff, icon: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM9 16l2 2 4-4' },
      { to: '/payroll', label: 'Payroll', roles: ROLES.admin, icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' },
      { to: '/certificates', label: 'Certificates', roles: ROLES.admin, icon: 'M12 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 15v6l-2-1.5L8 21M16 21l-2-1.5' },
    ],
  },
  {
    heading: 'Operations', key: 'ops',
    items: [
      { to: '/transport', label: 'Transport', roles: ROLES.transportMgmt, icon: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M4 16l-1 4M20 16l1 4M7 20v-2M17 20v-2M6 9h12' },
      { to: '/vehicle-costs', label: 'Vehicle costs', roles: ROLES.transportMgmt, icon: 'M3 12h18M12 3v18' },
      { to: '/gate', label: 'The gate', roles: ROLES.gate, icon: 'M12 2l7 4v6c0 5-3 8-7 10-4-2-7-5-7-10V6l7-4zM9 12l2 2 4-4' },
      { to: '/releases', label: 'Release requests', roles: ROLES.admin, icon: 'M9 12l2 2 4-4M12 3l8 4v6c0 4-3 7-8 9-5-2-8-5-8-9V7l8-4z' },
      { to: '/hostel', label: 'Hostel', roles: ROLES.hostel, icon: 'M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01' },
      { to: '/library', label: 'Library', roles: ROLES.library, icon: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z' },
      { to: '/store', label: 'Store & allocations', roles: ROLES.store, icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.3 2.3c-.6.6-.2 1.7.7 1.7H17M17 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM9 19a2 2 0 1 1-4 0 2 2 0 0 1 4 0z' },
    ],
  },
  {
    heading: 'Student life', key: 'life',
    items: [
      { to: '/houses', label: 'Houses & leadership', roles: ROLES.sports, icon: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01' },
      { to: '/trips', label: 'School trips', roles: ROLES.staff, icon: 'M3 12h18M3 12l4-8h10l4 8M3 12v6a1 1 0 0 0 1 1h1M21 12v6a1 1 0 0 0-1 1h-1M7 19a2 2 0 1 0 4 0M13 19a2 2 0 1 0 4 0' },
      { to: '/sports', label: 'Sports', roles: ROLES.sports, icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM2 12h20M12 2c3 3 3 17 0 20M12 2c-3 3-3 17 0 20' },
    ],
  },
  {
    heading: 'Communication', key: 'comms',
    items: [
      { to: '/announcements', label: 'Announcements', roles: ROLES.frontdesk, icon: 'M3 11l18-5v12L3 14v-3zM11.6 16.8a3 3 0 1 1-5.8-1.6' },
      { to: '/communication', label: 'Communication', roles: ROLES.frontdesk, icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
      { to: '/news', label: 'News & achievements', roles: ROLES.frontdesk, icon: 'M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2M18 14h-8M15 18h-5M10 6h8v4h-8z' },
      { to: '/usage', label: 'Messages & AI', roles: ROLES.admin, icon: 'M3 3v18h18M7 15l3-5 3 3 4-7' },
    ],
  },
  {
    // ⚠ TWO THINGS THAT SOUND ALIKE AND ARE NOT. "Reports" is the school pulling its OWN records
    // out as a spreadsheet. The other screen publishes circulars and forms TO FAMILIES. They sit
    // together deliberately — side by side the difference is legible, scattered it is not — and the
    // second is no longer labelled just "Downloads", because next to a page of download buttons
    // that word described both of them.
    heading: 'Files & reports', key: 'files',
    items: [
      { to: '/reports', label: 'Reports', roles: ROLES.staff, icon: 'M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM3 9h18M10 9v12' },
      { to: '/downloads-admin', label: 'Files for families', roles: ROLES.admin, icon: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' },
    ],
  },
  {
    heading: 'School settings', key: 'settings',
    items: [
      { to: '/all-campuses', label: 'All campuses', roles: ROLES.trust, icon: 'M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6M9 12h.01M15 12h.01' },
      { to: '/admin', label: 'Administration', roles: ROLES.admin, icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' },
      // ⚠ The one screen that can CORRECT the lists everything else refers to — subjects, classes,
      // rooms, departments, designations, leave types, vehicles, campuses, institutions. Until this
      // existed, every one of them could be created and never edited.
      { to: '/master-data', label: 'Master data', roles: ROLES.admin, icon: 'M4 6h16M4 12h16M4 18h10M18 15v6M15 18h6' },
      { to: '/branding', label: 'Branding', roles: ROLES.admin, icon: 'M12 2l2.4 7.4H22l-6 4.5 2.3 7.1-6.3-4.6-6.3 4.6L8 13.9 2 9.4h7.6z' },
      { to: '/public-page', label: 'Public page', roles: ROLES.admin, icon: 'M12 2a10 10 0 100 20 10 10 0 000-20zM2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20' },
      { to: '/holidays', label: 'Holidays', roles: ROLES.admin, icon: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' },
      { to: '/automation', label: 'Automation', roles: ROLES.admin, icon: 'M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-7.07l-2.83 2.83M8.76 15.24l-2.83 2.83m12.14 0l-2.83-2.83M8.76 8.76L5.93 5.93' },
      { to: '/privacy-notice', label: 'Privacy notice', roles: ROLES.admin, icon: 'M12 2l8 4v6c0 5-3 8-8 10-5-2-8-5-8-10V6l8-4zM9 12l2 2 4-4' },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { branding, moduleEnabled } = useTenant();
  // ⚠ STAFF ONLY. The endpoint is staff-authorised, so asking as a parent is a 403 on every page
  // load — and a parent has no capture entries in the menu to gate anyway.
  const { allows } = useCaptureModes(!!user?.role && (ROLES.staff as readonly string[]).includes(user.role));
  // Nav labels and section headings live in a data array, so they are translated at RENDER time.
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();

  // On a phone the sidebar is a drawer. It must close when you navigate —
  // otherwise you tap a link and stare at the menu you just used.
  const [navOpen, setNavOpen] = useState(false);
  // Which nav sections the user has explicitly toggled. Unset = follow the active route.
  const [openNav, setOpenNav] = useState<Record<string, boolean>>({});
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Escape closes it, like any other overlay.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ImpersonationBanner />
      <div className="shell">

      {/* scrim — phone only; tapping outside dismisses the drawer */}
      <div
        className={`shell-scrim ${navOpen ? 'is-open' : ''}`}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />

      {/* sidebar */}
      <aside className={`shell-aside ${navOpen ? 'is-open' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 20px' }}>
          <BrandMark size={34} />
          <div style={{ lineHeight: 1.2, overflow: 'hidden' }}>
            <div className="h-display" style={{ fontSize: 16, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {branding?.name ?? 'Eduvora'}
            </div>
            <div className="eyebrow" style={{ fontSize: 9 }}>Eduvora</div>
          </div>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {navGroups
            .map((group) => ({
              ...group,
              items: group.items.filter((n) =>
                (!user?.role || n.roles.includes(user.role)) &&
                moduleEnabled(ROUTE_MODULE[n.to] ?? '') &&
                (!n.capture || allows(n.capture))),
            }))
            .filter((group) => group.items.length > 0)
            .map((group, gi) => {
            // A section opens if the user opened it, or if the page you're on lives inside it —
            // so navigating never hides where you actually are.
            const holdsActive = group.items.some((n) =>
              n.end ? location.pathname === n.to : location.pathname.startsWith(n.to));
            const gk = group.key ?? `g${gi}`;
            const open = group.heading === null || (openNav[gk] ?? holdsActive);
            return (
            <div key={gk} style={{ marginTop: group.heading ? 10 : 0 }}>
              {group.heading && (
                <button
                  onClick={() => setOpenNav((s) => ({ ...s, [gk]: !open }))}
                  aria-expanded={open}
                  className="eyebrow"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    fontSize: 10, padding: '6px 12px', color: 'var(--ink-3)',
                    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                  {t(group.heading)}
                </button>
              )}
              {open && group.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px',
                    borderRadius: 'var(--r-md)', fontWeight: 500, fontSize: 14,
                    color: isActive ? 'var(--brand)' : 'var(--ink-2)',
                    background: isActive ? 'color-mix(in srgb, var(--accent) 14%, transparent)' : 'transparent',
                  })}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d={n.icon} />
                  </svg>
                  {t(n.label)}
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 12px' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 999, background: 'var(--surface-2)',
              border: '1px solid var(--line)', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13,
            }}>
              {(user?.displayName ?? 'U')[0].toUpperCase()}
            </div>
            <div style={{ lineHeight: 1.25, overflow: 'hidden' }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user?.displayName}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{user?.role}</div>
            </div>
          </div>
          <div style={{ padding: '0 8px 10px' }}>
            <LanguagePicker />
          </div>
          <button className="btn" style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => { logout(); navigate('/login'); }}>
            Sign out
          </button>
        </div>
      </aside>

      {/* main */}
      <div className="shell-main">
        <header className="topbar">
          <button
            className="shell-burger"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
          {/* ⚠ BEFORE the spacer, so it sits on the LEFT of the bar next to the school name rather
              than lost among the icons on the right. "Which campus am I looking at" is the first
              thing a trust admin needs to know on every page, not a utility. */}
          <CampusPicker />
          <div className="topbar-spacer" />
          <InstallButton />
          <NotificationBell />
          <ThemeToggle />
        </header>
        <main className="page" style={{ maxWidth: 1180, width: '100%', margin: '0 auto' }}>
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
